//
// The invitee turns the invite down with a note telling the inviter what to
// correct; the inviter edits the invite and sends it again.
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useState,
} from 'react';

import {
  Button,
  Column,
  TextInput,
} from '../../../components';
import {
  useAuth,
  useTownAssets,
} from '../../../stores';
import {
  config,
  logError,
  regex,
} from '../../../utils';

function InviteAckRefusal(props) {
  const {
    piid,
    inviter,
    onRefused,
  } = props;
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { auth } = useAuth();
  const { superagentPostAsync } = auth;

  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const trimmedNote = note.trim();
  const noteValid = Boolean(trimmedNote) && regex.text1000charsMultiline.test(trimmedNote);

  const refuse = useCallback(
    async () => {
      setPending(true);
      setFailed(false);

      let refused = false;
      try {
        const res = await superagentPostAsync(
          config.town.back.urlPrefix + config.town.back.invite.refuse,
          ['Content-Type', 'application/json'],
          {
            piid,
            refusalNote: trimmedNote,
          },
        );

        // 200 with no data is the refusal; any object names why it was not taken
        refused = (res?.status === 200) && (!res.body?.rslt?.data?.[0]);
      } catch (err) {
        logError(
          `InviteAckRefusal ${err?.message ?? err}`,
          'InviteAckRefusal.refuse.error',
          { piid },
        );
      }

      setPending(false);

      if (refused) onRefused();
      else setFailed(true);
    },
    [superagentPostAsync, piid, trimmedNote, onRefused],
  );

  if (!open) {
    return (
      <Button
        id="refuseInvite"
        label={translate('refuseInvite')}
        onClick={() => setOpen(true)}
        criticalColor
        tiny
      />
    );
  }

  return (
    <Column crossStretch gap={2} className="w-full">
      <span>{translate('inviteRefusalNoteHint', 'statements', { inviter })}</span>

      <TextInput
        id="refusalNote"
        name="refusalNote"
        value={note}
        onChange={(event) => setNote(event.currentTarget.value)}
        invalid={Boolean(note) && (!noteValid)}
        multiline
        focus
      />

      {(failed)
        ? <span className="text-pxUiAlert italic">{translate('inviteRefuseError', 'statements')}</span>
        : null}

      <Button
        id="sendRefusal"
        label={translate('refuseInvite')}
        onClick={refuse}
        disabled={!noteValid}
        pending={pending}
        criticalColor
      />
    </Column>
  );
}

InviteAckRefusal.propTypes = {
  piid: PropTypes.string.isRequired,
  inviter: PropTypes.string,
  onRefused: PropTypes.func.isRequired,
};

export default InviteAckRefusal;
